import { activities as activitiesData, companies as companiesData, projects } from "../../../db";
import type { Project } from "../pages/ProjectPage";
import {
  calculateDurationInMonths,
  calculateCompanyAttributedProjectDuration,
  normalizeSkillFormat,
  createSkillMaps,
  addSkillToMap,
  normalizeSkillMaps,
  convertToLegacyFormat,
  type SkillMaps,
  type NormalizedSkillsByCategory
} from './skills/calculators/skillCalculationUtils';

export interface CompanyStats {
  id: number;
  name: string;
  logoPath: string;
  logoText: string;
  slug: string;
  startDate: string;
  endDate: string | null;
  totalDuration: number; // in months
  website?: string; // Add website property
  photoPath?: string; // Add photoPath property
  mapsLink?: string; // Add mapsLink property
  activities: Array<{
    id: number;
    title: string;
    type: string;
    startDate: string;
    endDate: string | null;
    companyId: number;
    department?: string;
    position?: string;
    description?: string;
    gpa?: string;
    degree?: string;
  }>;
  skillsByCategory: {
    profession: Array<{ name: string; level: number; }>;
    lead: Array<{ name: string; level: number; }>;
    language: Array<{ name: string; level: number; }>;
    tools: Array<{ name: string; level: number; }>;
  };
  // Legacy fields for backward compatibility
  skills: Array<{ name: string; level: number; }>;
  description: string;
}

/**
 * Generate URL-friendly slug from company name
 */
export function createCompanySlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '');
}

/**
 * Get company description based on activities
 */
function generateCompanyDescription(companyName: string, activities: typeof activitiesData): string {
  const workActivities = activities.filter(a => a.type === 'work');
  const educationActivities = activities.filter(a => a.type === 'education');
  
  if (workActivities.length > 0 && educationActivities.length > 0) {
    return `Professional and educational experience at ${companyName}, combining practical work experience with academic learning across multiple roles and projects.`;
  } else if (workActivities.length > 0) {
    return `Professional work experience at ${companyName}, contributing to various projects and initiatives across ${workActivities.length} role${workActivities.length > 1 ? 's' : ''}.`;
  } else if (educationActivities.length > 0) {
    return `Educational experience at ${companyName}, focusing on academic learning and skill development through ${educationActivities.length} program${educationActivities.length > 1 ? 's' : ''}.`;
  }
  
  return `Experience at ${companyName} across various activities and projects.`;
}

/**
 * Calculate skills and tech gained from company activities
 */
export function calculateCompanySkillsAndTech(companyActivities: Array<{
  id: number;
  title: string;
  type: string;
  startDate: string;
  endDate: string | null;
  companyId: number;
  department?: string;
  position?: string;
  description?: string;
  gpa?: string;
  degree?: string;
}>): { 
  skillsByCategory: NormalizedSkillsByCategory;
  skills: Array<{ name: string; level: number; }>; 
} {
  const skillMaps = createSkillMaps();
  
  // Find all projects associated with company activities
  const activityIds = companyActivities.map((a: any) => a.id);
  
  // Find all projects that have any activity at this company
  // Exclude private projects (activityId === null)
  const companyProjects = projects.filter((p: any) => {
    // Skip private projects
    if (p.activityId === null) {
      return false;
    }
    
    if (Array.isArray(p.activityId)) {
      // If project has multiple activities, check if any belong to this company
      return p.activityId.some((id: number) => activityIds.includes(id));
    }
    // If project has single activity, check if it belongs to this company
    return p.activityId && activityIds.includes(p.activityId);
  });
  
  // Calculate total attributed project duration for proper normalization
  let totalAttributedProjectMonths = 0;
  
  companyProjects.forEach((project: any) => {
    // Calculate how much of this project's duration should be attributed to this company
    const attributedDuration = calculateCompanyAttributedProjectDuration(
      project,
      activityIds,
      companyActivities
    );
    
    // Add to total attributed project duration for normalization
    totalAttributedProjectMonths += attributedDuration;
    
    // Get normalized skill format (handles both legacy and new formats)
    const normalizedSkills = normalizeSkillFormat(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, attributedDuration, skill.weight);
      });
    });
  });
  
  // Calculate normalized skill levels (0-100) for each category
  // Important: Normalize against total attributed project duration for accurate percentage representation
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalAttributedProjectMonths);

  // Convert to legacy format for backward compatibility
  const { skills } = convertToLegacyFormat(skillsByCategory);
  
  return {
    skillsByCategory,
    skills
  };
}

/**
 * Get all company statistics
 */
export function getCompanyStats(companyId: number): CompanyStats | null {
  const company = companiesData.find(c => c.id === companyId);
  if (!company) return null;
  
  // Get activities where this company is either the main company or the temp agency
  const companyActivities = activitiesData.filter(a => 
    a.companyId === companyId || a.tempAgencyId === companyId
  );
  if (companyActivities.length === 0) return null;
  
  // Calculate date range
  const startDates = companyActivities.map(a => a.startDate).sort();
  const endDates = companyActivities
    .map(a => a.endDate)
    .filter(date => date !== null)
    .sort();
  
  const startDate = startDates[0];
  const endDate = companyActivities.some(a => a.endDate === null) ? null : endDates[endDates.length - 1];
  
  const totalDuration = calculateDurationInMonths(startDate, endDate);
  const { skillsByCategory, skills } = calculateCompanySkillsAndTech(companyActivities);
  const description = generateCompanyDescription(company.name, companyActivities);
  
  return {
    ...company,
    slug: createCompanySlug(company.name),
    startDate,
    endDate,
    totalDuration,
    activities: companyActivities,
    skillsByCategory,
    skills,
    description
  };
}

/**
 * Get all companies with their stats
 * Memoized for performance since this can be an expensive operation
 */
const memoizedCompaniesCache = new Map<string, CompanyStats[]>();

export function getAllCompaniesStats(): CompanyStats[] {
  // Simple cache key based on companies data length and activities length
  const cacheKey = `${companiesData.length}-${activitiesData.length}`;
  
  if (memoizedCompaniesCache.has(cacheKey)) {
    return memoizedCompaniesCache.get(cacheKey)!;
  }
  
  const result = companiesData
    .map(company => getCompanyStats(company.id))
    .filter((stats): stats is CompanyStats => stats !== null);
  
  memoizedCompaniesCache.set(cacheKey, result);
  return result;
}

/**
 * Get company by slug
 */
export function getCompanyBySlug(slug: string): CompanyStats | null {
  const allCompanies = getAllCompaniesStats();
  return allCompanies.find(company => company.slug === slug) || null;
}

/**
 * Format duration for display
 */
export function formatDuration(months: number): string {
  if (months < 12) {
    return `${months} month${months !== 1 ? 's' : ''}`;
  }
  
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  let result = `${years} year${years !== 1 ? 's' : ''}`;
  if (remainingMonths > 0) {
    result += ` ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  }
  
  return result;
}

/**
 * Format date range for display
 */
export function formatDateRange(startDate: string, endDate: string | null): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  
  const startStr = start.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short' 
  });
  
  const endStr = end ? end.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short' 
  }) : 'Present';
  
  return `${startStr} - ${endStr}`;
}
